import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, take, catchError } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
  AcquireAllLocksAndInitImageStore,
  AcquireLockAndAddImagesBucketAndCommitAndThenReleaseLock,
  AcquireLockAndBackfillMissingServerImagesBucketList,
  AcquireLockAndThenSyncImagesBucketAndThenReleaseLock,
  AddImagesBucketAndThenReleaseLock,
  AddImagesBucketFromBucketIdsOnBulkAndThenReleaseLock,
  AcquireLockAndAddImagesBucketOnBulkAndThenReleaseLock,
  BackfillMissingServerImagesBucketListAndReleaseLock,
  CommitImagesBucketAndThenAddImagesBucketAndThenReleaseLock,
  getInitialImageStore,
  ImageDBNoAction,
  IMAGES_UPLOAD_RETRY_COUNT,
  InitImageStoreAndReleaseAllLocks,
  InitStoreFacade,
  InputAppClientDBFacade,
  InputAppImageDBActionTypes,
  InputAppImageDBFacade,
  PersistentStorageService,
  ReleaseImageDBLock,
  ReleaseImageDBLockAndImageMetadataDBLock,
  ReleaseImageMetadataDBLock,
  ServerDefectivesStoreFacade,
  SyncImagesBucketAndThenReleaseLock,
  UpdateImagesBucketDBAndReleaseLock,
  UpdateImagesBucketMetadataDBAndReleaseLock,
  UpdateImagesStoreAllDBsAndReleaseAllLocks,
  UpdateServerImagesBucketDBAndReleaseLock,
} from '@monorepo/core-data';
import { BucketStorageService, CustomLoggerService } from '@monorepo/services';
import { BCTXComputationHelper, EventUtil, InputAppAPIHelperUtil, InputAppErrorDisplay } from '@monorepo/utils';
import produce from 'immer';
import { TBucketId } from 'libs/services/src/lib/capacitor/bucket-storage.types';
import { TImagesBucket } from '@monorepo/types';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class InputAppImageDBEffects {
  constructor(
    private action$: Actions,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private persistentStorageService: PersistentStorageService,
    private bucketStorageService: BucketStorageService,
    private inputAppImageDBFacade: InputAppImageDBFacade,
    private serverDefectiveStoreFacade: ServerDefectivesStoreFacade,
    private initStoreFacade: InitStoreFacade,
    private translate: TranslateService,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private inputAppErrorUtil: InputAppErrorDisplay,
    private logger: CustomLoggerService,
  ) {}

  AcquireAllLocksAndInitImageStore$ = createEffect(() =>
    this.action$.pipe(
      ofType<AcquireAllLocksAndInitImageStore>(InputAppImageDBActionTypes.AcquireAllLocksAndInitImageStore),
      switchMap((action) =>
        this.inputAppImageDBFacade.acquireLockForImagesBucketAddition('PRE_INIT_PENDING').pipe(
          take(1),
          switchMap((res) => {
            if (res.status === 'SUCCESS') {
              return this.inputAppImageDBFacade.acquireLockForSyncedImagesBucketIdsAddition('PRE_INIT_PENDING').pipe(
                take(1),
                switchMap((res) => {
                  if (res.status === 'SUCCESS') {
                    return this.inputAppImageDBFacade
                      .acquireLockForServerImagesBucketIdsAddition('PRE_INIT_PENDING')
                      .pipe(
                        take(1),
                        map((res) => {
                          if (res.status === 'SUCCESS') {
                            return new InitImageStoreAndReleaseAllLocks(action.initBy);
                          } else return new ReleaseImageDBLockAndImageMetadataDBLock();
                        }),
                      );
                  } else return of(new ReleaseImageDBLock());
                }),
              );
            } else {
              return of(new ImageDBNoAction());
            }
          }),
        ),
      ),
    ),
  );

  InitImageStoreAndReleaseAllLocks$ = createEffect(() =>
    this.action$.pipe(
      ofType<InitImageStoreAndReleaseAllLocks>(InputAppImageDBActionTypes.InitImageStoreAndReleaseAllLocks),
      switchMap((action) => {
        return this.initStoreFacade.getNextCacheResetTimestamp().pipe(
          take(1),
          map((nextCacheResetTimestamp) => {
            return [nextCacheResetTimestamp, action] as const;
          }),
        );
      }),
      switchMap(([nextCacheResetTimestamp, action]) => {
        const imageStore = getInitialImageStore(nextCacheResetTimestamp);
        return from(this.persistentStorageService.get('[APP][INPUT_APP_SERVER_IMAGES_BUCKET_DB]')).pipe(
          take(1),
          switchMap((res) => {
            if (!res || res.nextCacheResetTimestamp !== nextCacheResetTimestamp) {
              return from(
                this.persistentStorageService.set(
                  '[APP][INPUT_APP_SERVER_IMAGES_BUCKET_DB]',
                  imageStore.serverImagesBucketDB,
                ),
              ).pipe(
                take(1),
                switchMap(() => {
                  return from(this.bucketStorageService.removeServerDefectivesDirectory()).pipe(
                    take(1),
                    map(() => [imageStore.serverImagesBucketDB, nextCacheResetTimestamp, action] as const),
                  );
                }),
              );
            } else {
              return of([res, nextCacheResetTimestamp, action] as const);
            }
          }),
        );
      }),
      switchMap(([updatedServerImagesBucketDB, nextCacheResetTimestamp, action]) => {
        const imageStore = getInitialImageStore(nextCacheResetTimestamp);
        return from(this.persistentStorageService.get('[APP][INPUT_APP_IMAGES_BUCKET_DB]')).pipe(
          take(1),
          switchMap((res) => {
            if (!res || res.nextCacheResetTimestamp !== nextCacheResetTimestamp) {
              return from(
                this.persistentStorageService.set('[APP][INPUT_APP_IMAGES_BUCKET_DB]', imageStore.imagesBucketDB),
              ).pipe(
                take(1),
                map(() => {
                  return [
                    updatedServerImagesBucketDB,
                    imageStore.imagesBucketDB,
                    !res ? [] : res.imageBuckets.list,
                    nextCacheResetTimestamp,
                    action,
                  ] as const;
                }),
              );
            } else {
              return of([
                updatedServerImagesBucketDB,
                res,
                res.imageBuckets.list,
                nextCacheResetTimestamp,
                action,
              ] as const);
            }
          }),
        );
      }),
      switchMap(
        ([updatedServerImagesBucketDB, updatedImagesBucketDB, imagesBucketList, nextCacheResetTimestamp, action]) => {
          const imageStore = getInitialImageStore(nextCacheResetTimestamp);
          return from(this.persistentStorageService.get('[APP][INPUT_APP_IMAGES_BUCKET_METADATA_DB]')).pipe(
            take(1),
            switchMap((res) => {
              const bucketInfo = produce(
                !res ? imageStore.imagesBucketMetadataDB.bucketsInfo : res.bucketsInfo,
                (draft) => {
                  imagesBucketList.forEach((bucketInfo) => {
                    if (!draft[bucketInfo.bucketId]) {
                      draft[bucketInfo.bucketId] = {
                        status: 'YET_TO_BE_SYNCED',
                        remainingRetryCount: IMAGES_UPLOAD_RETRY_COUNT,
                        eventTime: bucketInfo.eventTime,
                      };
                    }
                  });
                },
              );
              if (res && res.nextCacheResetTimestamp === nextCacheResetTimestamp) {
                return from(
                  this.persistentStorageService.set('[APP][INPUT_APP_IMAGES_BUCKET_METADATA_DB]', {
                    nextCacheResetTimestamp,
                    bucketsInfo: bucketInfo,
                  }),
                ).pipe(
                  take(1),
                  map(() => {
                    return new UpdateImagesStoreAllDBsAndReleaseAllLocks(
                      updatedServerImagesBucketDB,
                      updatedImagesBucketDB,
                      { nextCacheResetTimestamp, bucketsInfo: bucketInfo },
                      action.initBy,
                    );
                  }),
                );
              } else {
                const validBucketIds: TBucketId[] = [];
                const invalidBucketIds: TBucketId[] = [];
                Object.entries(bucketInfo).forEach(([bucketId, metadata]) => {
                  if (metadata.status === 'YET_TO_BE_SYNCED') validBucketIds.push(bucketId as TBucketId);
                  else invalidBucketIds.push(bucketId as TBucketId);
                });
                const updatedBucketInfo = produce(bucketInfo, (draft) => {
                  invalidBucketIds.forEach((invalidBucketId) => {
                    delete draft[invalidBucketId];
                  });
                });
                return from(this.bucketStorageService.removeAllDefectiveFilesExceptBucketIds(validBucketIds)).pipe(
                  take(1),
                  switchMap(() => {
                    return from(
                      this.persistentStorageService.set('[APP][INPUT_APP_IMAGES_BUCKET_METADATA_DB]', {
                        nextCacheResetTimestamp,
                        bucketsInfo: updatedBucketInfo,
                      }),
                    ).pipe(
                      take(1),
                      map(() => {
                        return new UpdateImagesStoreAllDBsAndReleaseAllLocks(
                          updatedServerImagesBucketDB,
                          updatedImagesBucketDB,
                          { nextCacheResetTimestamp, bucketsInfo: updatedBucketInfo },
                          action.initBy,
                        );
                      }),
                    );
                  }),
                );
              }
            }),
          );
        },
      ),
    ),
  );

  UpdateImagesStoreAllDBsAndReleaseAllLocks$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateImagesStoreAllDBsAndReleaseAllLocks>(
        InputAppImageDBActionTypes.UpdateImagesStoreAllDBsAndReleaseAllLocks,
      ),
      map((action) => {
        this.serverDefectiveStoreFacade.init(action.initBy);
        return new ImageDBNoAction();
      }),
    ),
  );

  AcquireLockAndBackfillMissingServerImagesBucketList$ = createEffect(() =>
    this.action$.pipe(
      ofType<AcquireLockAndBackfillMissingServerImagesBucketList>(
        InputAppImageDBActionTypes.AcquireLockAndBackfillMissingServerImagesBucketList,
      ),
      switchMap((action) =>
        this.inputAppImageDBFacade
          .acquireLockForServerImagesBucketIdsAddition('BACKGROUND_SERVER_IMAGE_BACKFILLING')
          .pipe(
            take(1),
            map((res) => {
              if (res.status === 'SUCCESS') {
                return new BackfillMissingServerImagesBucketListAndReleaseLock();
              } else {
                return new ImageDBNoAction();
              }
            }),
          ),
      ),
    ),
  );

  BackfillMissingServerImagesBucketListAndReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<BackfillMissingServerImagesBucketListAndReleaseLock>(
        InputAppImageDBActionTypes.BackfillMissingServerImagesBucketListAndReleaseLock,
      ),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_IMAGES_BUCKET_DB]')).pipe(
          take(1),
          map((res) => {
            return [res] as const;
          }),
        ),
      ),
      switchMap(([imagesBucketDB]) => {
        return from(this.persistentStorageService.get('[APP][INPUT_APP_SERVER_IMAGES_BUCKET_DB]')).pipe(
          take(1),
          map((serverImagesBucketDB) => {
            return [imagesBucketDB, serverImagesBucketDB] as const;
          }),
        );
      }),
      switchMap(([imagesBucketDB, serverImagesBucketDB]) => {
        return this.serverDefectiveStoreFacade.getServerDefectivesForAllSubjects().pipe(
          take(1),
          switchMap((serverDefectives) => {
            const toBeAddedBucketIdsWithoutFiles: TImagesBucket[] = [];
            serverDefectives.forEach((serverDefective) => {
              const imageId = serverDefective.imageId;
              if (BCTXComputationHelper.isTodayOrYesterday(serverDefective.eventInfo.eventTime) && imageId) {
                if (
                  !serverImagesBucketDB.imageBuckets.list.some((bucketId) => bucketId.bucketId === imageId) &&
                  !imagesBucketDB.imageBuckets.list.some((bucketInfo) => bucketInfo.bucketId === imageId)
                ) {
                  toBeAddedBucketIdsWithoutFiles.push({
                    bucketId: imageId,
                    eventTime: serverDefective.eventInfo.eventTime,
                    files: [],
                  });
                }
              }
            });
            if (toBeAddedBucketIdsWithoutFiles.length === 0)
              return of(new UpdateServerImagesBucketDBAndReleaseLock(serverImagesBucketDB));
            else {
              return from(this.inputAppImageDBFacade.backfillImagesByBucketIds(toBeAddedBucketIdsWithoutFiles)).pipe(
                take(1),
                switchMap((res) => {
                  const updatedServerImagesBucketDB = produce(serverImagesBucketDB, (draft) => {
                    res.forEach((addedBucketInfo) => {
                      draft.imageBuckets.list.push(addedBucketInfo);
                    });
                  });
                  return from(
                    this.persistentStorageService.set(
                      '[APP][INPUT_APP_SERVER_IMAGES_BUCKET_DB]',
                      updatedServerImagesBucketDB,
                    ),
                  ).pipe(
                    take(1),
                    map(() => {
                      return new UpdateServerImagesBucketDBAndReleaseLock(updatedServerImagesBucketDB);
                    }),
                  );
                }),
              );
            }
          }),
        );
      }),
    ),
  );

  AcquireLockAndAddImagesBucketAndCommitAndThenReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<AcquireLockAndAddImagesBucketAndCommitAndThenReleaseLock>(
        InputAppImageDBActionTypes.AcquireLockAndAddImagesBucketAndCommitAndThenReleaseLock,
      ),
      switchMap((action) =>
        this.inputAppImageDBFacade.acquireLockForImagesBucketAddition('IMAGE_ADDITION_BY_USER').pipe(
          take(1),
          map((res) => {
            if (res.status === 'SUCCESS') {
              return new CommitImagesBucketAndThenAddImagesBucketAndThenReleaseLock(
                action.bucketInfo,
                action.commitId,
                action.eventTime,
              );
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translate.instant('input_app_common.error'), res.message);
              return new ImageDBNoAction();
            }
          }),
        ),
      ),
    ),
  );

  AcquireLockAndAddImagesBucketOnBulkAndThenReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<AcquireLockAndAddImagesBucketOnBulkAndThenReleaseLock>(
        InputAppImageDBActionTypes.AcquireLockAndAddImagesBucketOnBulkAndThenReleaseLock,
      ),
      switchMap((action) => {
        if (action.bucketIds.length === 0) {
          return of(new ImageDBNoAction());
        }
        return this.inputAppImageDBFacade.acquireLockForImagesBucketAddition('IMAGE_ADDITION_BY_USER').pipe(
          take(1),
          map((res) => {
            if (res.status === 'SUCCESS') {
              return new AddImagesBucketFromBucketIdsOnBulkAndThenReleaseLock(action.bucketIds, action.eventTime);
            } else {
              this.inputAppAPIHelperUtil.showPopup(this.translate.instant('input_app_common.error'), res.message);
              return new ImageDBNoAction();
            }
          }),
        );
      }),
    ),
  );

  CommitImagesBucketAndThenAddImagesBucketAndThenReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<CommitImagesBucketAndThenAddImagesBucketAndThenReleaseLock>(
        InputAppImageDBActionTypes.CommitImagesBucketAndThenAddImagesBucketAndThenReleaseLock,
      ),
      switchMap((action) => {
        return from(this.bucketStorageService.commitBucket(action.commitId, action.bucketInfo)).pipe(
          take(1),
          map((res) => {
            if (res.status) return new AddImagesBucketAndThenReleaseLock(res.updatedBucket, action.eventTime);
            else {
              this.inputAppAPIHelperUtil.showPopup(
                this.translate.instant('input_app_common.error'),
                this.translate.instant('input_app_image_db_effects.storingImageError'),
              );
              return new ReleaseImageDBLock();
            }
          }),
        );
      }),
    ),
  );

  AddImagesBucketAndThenReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<AddImagesBucketAndThenReleaseLock>(InputAppImageDBActionTypes.AddImagesBucketAndThenReleaseLock),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_IMAGES_BUCKET_DB]')).pipe(
          take(1),
          map((res) => {
            const updatedDB = produce(res, (draft) => {
              draft.imageBuckets.list.push({
                files: action.bucketInfo.files.map((fileInfo) => {
                  return {
                    publicPath: fileInfo.publicPath,
                  };
                }),
                bucketId: action.bucketInfo.id,
                eventTime: action.eventTime,
              });
            });
            return [updatedDB, action] as const;
          }),
        ),
      ),
      switchMap(([updatedDB, action]) => {
        return from(this.persistentStorageService.set('[APP][INPUT_APP_IMAGES_BUCKET_DB]', updatedDB)).pipe(
          take(1),
          map(() => {
            return new UpdateImagesBucketDBAndReleaseLock(updatedDB);
          }),
        );
      }),
    ),
  );

  AddImagesBucketFromBucketIdsOnBulkAndThenReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<AddImagesBucketFromBucketIdsOnBulkAndThenReleaseLock>(
        InputAppImageDBActionTypes.AddImagesBucketFromBucketIdsOnBulkAndThenReleaseLock,
      ),
      switchMap((action) => {
        const bucketPromises = action.bucketIds.map(async (bucketId) => {
          const publicFiles = await this.bucketStorageService.getAllDefectiveFilesByBucketId(bucketId);
          if (publicFiles && publicFiles.length > 0) {
            return {
              bucketId,
              eventTime: action.eventTime,
              files: publicFiles.map((file) => ({
                publicPath: file.publicPath,
              })),
            } as TImagesBucket;
          }
          return null;
        });
        return from(Promise.allSettled(bucketPromises)).pipe(
          take(1),
          switchMap((settledResults) => {
            const imageBuckets: TImagesBucket[] = [];
            for (const result of settledResults) {
               if (result?.status === 'fulfilled' && result.value !== null) {
                imageBuckets.push(result.value);
              }
            }
            if (imageBuckets.length < action.bucketIds.length) {
              const fetchedIds: TBucketId[] = imageBuckets.map((bucket) => bucket.bucketId);
              this.logger.error('[ImagesBucketDB] Failed fetching files for some buckets', {
                allBucketIds: action.bucketIds,
                fetchedBucketIds: fetchedIds,
              }); 
            }
            if (imageBuckets.length === 0) {
              return of(new ReleaseImageDBLock());
            }
            return from(this.persistentStorageService.get('[APP][INPUT_APP_IMAGES_BUCKET_DB]')).pipe(
              take(1),
              map((res) => {
                const updatedDB = produce(res, (draft) => {
                  draft.imageBuckets.list.push(...imageBuckets);
                });
                return updatedDB;
              }),
              switchMap((updatedDB) => {
                return from(this.persistentStorageService.set('[APP][INPUT_APP_IMAGES_BUCKET_DB]', updatedDB)).pipe(
                  take(1),
                  map(() => new UpdateImagesBucketDBAndReleaseLock(updatedDB)),
                );
              }),
            );
          }),
          catchError((err) => {
            this.logger.error('[ImagesBucketDB] Failed updating DB', {
              errorMessage: err?.message,
              errorCode: err?.code,
              status: err?.status,
            });
            return of(new ReleaseImageDBLock());
          }),
        );
      }),
    ),
  );

  UpdateImagesBucketDBAndReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateImagesBucketDBAndReleaseLock>(InputAppImageDBActionTypes.UpdateImagesBucketDBAndReleaseLock),
      switchMap(() => {
        this.inputAppImageDBFacade.acquireLockAndSyncImages();
        return of(new ImageDBNoAction());
      }),
    ),
  );

  AcquireLockAndThenSyncImagesBucketAndThenReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<AcquireLockAndThenSyncImagesBucketAndThenReleaseLock>(
        InputAppImageDBActionTypes.AcquireLockAndThenSyncImagesBucketAndThenReleaseLock,
      ),
      switchMap((action) =>
        this.inputAppImageDBFacade.acquireLockForSyncedImagesBucketIdsAddition('BACKGROUND_IMAGE_SYNCING').pipe(
          take(1),
          switchMap((res) => {
            if (res.status === 'SUCCESS') {
              if (action.chainActions.appUpdate) {
                return this.inputAppClientDBFacade.acquireUserDBLock('SYNCING_DEFECTIVE_IMAGES').pipe(
                  take(1),
                  map((userDBLock) => {
                    if (userDBLock.status === 'SUCCESS')
                      return new SyncImagesBucketAndThenReleaseLock(action.chainActions);
                    else {
                      this.inputAppAPIHelperUtil.showPopup(
                        this.translate.instant('input_app_common.error'),
                        userDBLock.message,
                      );
                      return new ReleaseImageMetadataDBLock();
                    }
                  }),
                );
              } else return of(new SyncImagesBucketAndThenReleaseLock(action.chainActions));
            } else {
              return of(new ImageDBNoAction());
            }
          }),
        ),
      ),
    ),
  );

  SyncImagesBucketAndThenReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<SyncImagesBucketAndThenReleaseLock>(InputAppImageDBActionTypes.SyncImagesBucketAndThenReleaseLock),
      switchMap((action) => {
        if (action.chainActions.appUpdate) {
          return from(
            this.inputAppErrorUtil.presentLoadingWithId(
              'SYNCING_PENDING_DEFECTIVE_IMAGES',
              'Syncing Pending Defective Images...',
            ),
          ).pipe(
            take(1),
            map(() => [action] as const),
          );
        } else return of([action] as const);
      }),
      switchMap(([action]) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_IMAGES_BUCKET_DB]')).pipe(
          take(1),
          map((res) => {
            return [res, action] as const;
          }),
        ),
      ),
      switchMap(([imageBucketsDB, action]) => {
        return from(this.persistentStorageService.get('[APP][INPUT_APP_IMAGES_BUCKET_METADATA_DB]')).pipe(
          take(1),
          map((syncedImagesBucketDB) => {
            return [imageBucketsDB, syncedImagesBucketDB, action] as const;
          }),
        );
      }),
      switchMap(([imageBucketsDB, syncedImagesBucketDB, action]) =>
        this.inputAppImageDBFacade.getConfigurations().pipe(
          take(1),
          map(({ cacheResetTimestampInMilliSeconds, delayedEventsSLAInDays }) => {
            return [
              imageBucketsDB,
              syncedImagesBucketDB,
              cacheResetTimestampInMilliSeconds,
              delayedEventsSLAInDays,
              action,
            ] as const;
          }),
        ),
      ),
      switchMap(
        ([updatedDB, syncedImagesBucketDB, cacheResetTimestampInMilliSeconds, delayedEventsSLAInDays, action]) => {
          const delayedBorder = EventUtil.getDelayedEventsBorderTime(
            delayedEventsSLAInDays,
            cacheResetTimestampInMilliSeconds,
          );
          const toBeSyncedImagesBucketList: TBucketId[] = [];
          const delayedBucketIds: TBucketId[] = [];
          updatedDB.imageBuckets.list.forEach((imageBucket) => {
            if (!syncedImagesBucketDB.bucketsInfo[imageBucket.bucketId]) {
              syncedImagesBucketDB = produce(syncedImagesBucketDB, (draft) => {
                draft.bucketsInfo[imageBucket.bucketId] = {
                  status: 'YET_TO_BE_SYNCED',
                  remainingRetryCount: IMAGES_UPLOAD_RETRY_COUNT,
                  eventTime: imageBucket.eventTime,
                };
              });
            }
          });
          Object.entries(syncedImagesBucketDB.bucketsInfo).forEach(([bucketId, bucketMetadata]) => {
            if (bucketMetadata.status === 'YET_TO_BE_SYNCED') {
              if (bucketMetadata.eventTime < delayedBorder) {
                delayedBucketIds.push(bucketId as TBucketId);
              } else toBeSyncedImagesBucketList.push(bucketId as TBucketId);
            }
          });
          syncedImagesBucketDB = produce(syncedImagesBucketDB, (draft) => {
            delayedBucketIds.forEach((bucketId) => {
              draft.bucketsInfo[bucketId].status = 'DELAYED';
            });
          });
          return from(this.inputAppImageDBFacade.uploadAllBuckets(toBeSyncedImagesBucketList)).pipe(
            take(1),
            switchMap((res) => {
              toBeSyncedImagesBucketList.forEach((bucketId) => {
                if (
                  res[bucketId] === 'SYNCED' ||
                  res[bucketId] === 'SYNCED_WITH_FAILURE' ||
                  syncedImagesBucketDB.bucketsInfo[bucketId].remainingRetryCount === 0
                ) {
                  syncedImagesBucketDB = produce(syncedImagesBucketDB, (draft) => {
                    draft.bucketsInfo[bucketId].status = res[bucketId];
                  });
                } else {
                  syncedImagesBucketDB = produce(syncedImagesBucketDB, (draft) => {
                    draft.bucketsInfo[bucketId].remainingRetryCount =
                      draft.bucketsInfo[bucketId].remainingRetryCount - 1;
                  });
                }
              });
              return from(
                this.persistentStorageService.set('[APP][INPUT_APP_IMAGES_BUCKET_METADATA_DB]', syncedImagesBucketDB),
              ).pipe(
                take(1),
                map(() => {
                  return [syncedImagesBucketDB, action] as const;
                }),
              );
            }),
          );
        },
      ),
      switchMap(([syncedImagesBucketDB, action]) => {
        if (action.chainActions.appUpdate) {
          return from(this.inputAppErrorUtil.dismissLoadingWithId('SYNCING_PENDING_DEFECTIVE_IMAGES')).pipe(
            take(1),
            map(() => {
              this.inputAppClientDBFacade.releaseUserDBLock();
              return new UpdateImagesBucketMetadataDBAndReleaseLock(syncedImagesBucketDB, action.chainActions);
            }),
          );
        } else return of(new UpdateImagesBucketMetadataDBAndReleaseLock(syncedImagesBucketDB, action.chainActions));
      }),
    ),
  );

  UpdateImagesBucketMetadataDBAndReleaseLock$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateImagesBucketMetadataDBAndReleaseLock>(
        InputAppImageDBActionTypes.UpdateImagesBucketMetadataDBAndReleaseLock,
      ),
      switchMap((action) => {
        if (action.chainActions.appUpdate)
          this.inputAppImageDBFacade.updateAppAfterImageSync(action.chainActions.appUpdate);
        return of(new ImageDBNoAction());
      }),
    ),
  );
}
